// src/client/components/Footer.jsx
import React, { useMemo } from 'react';
import clientConfig from '../config/clientConfig';

const Footer = ({ theme }) => {
  const year = useMemo(() => new Date().getFullYear(), []);
  const footer = clientConfig.texts?.footer || {};

  /* =========================
     THEME 1 — PREMIUM PATCH
  ========================== */
  if (theme === 'theme1') {
    const hasSocial =
      !!footer.facebookUrl || !!footer.instagramUrl || !!footer.uberEatsUrl;

    return (
      <footer className="theme1-footer">
        <div className="theme1-footer-inner">
          {/* Brand / mini intro */}
          <div className="theme1-footer-top">
            <div className="theme1-footer-brand">
              <div className="theme1-footer-logo">
                {clientConfig.name || 'Restaurant'}
              </div>
              <p className="theme1-footer-tagline">
                {footer.tagline || "Cuisine maison • Ambiance chaleureuse • Service soigné"}
              </p>
            </div>

            {/* Quick CTA */}
            <div className="theme1-footer-cta">
              <a className="theme1-footer-btn" href="#reservation">
                {footer.cta || "Réserver"}
              </a>
              <a className="theme1-footer-btn theme1-footer-btn--ghost" href="#contact">
                {footer.cta2 || "Nous contacter"}
              </a>
            </div>
          </div>

          <div className="theme1-footer-grid">
            {/* Infos */}
            <div className="theme1-footer-card">
              <h3 className="theme1-footer-title">
                {footer.infoTitle || 'Informations'}
              </h3>

              <ul className="theme1-footer-list">
                <li>
                  <span className="theme1-footer-ico">📍</span>
                  <span>{footer.address || 'Adresse non renseignée'}</span>
                </li>
                <li>
                  <span className="theme1-footer-ico">📞</span>
                  <span>{footer.phone || 'Téléphone non renseigné'}</span>
                </li>
                <li>
                  <span className="theme1-footer-ico">🕒</span>
                  <span className="theme1-footer-hours">
                    {footer.hours || 'Horaires non renseignés'}
                  </span>
                </li>
              </ul>
            </div>

            {/* Social */}
            <div className="theme1-footer-card">
              <h3 className="theme1-footer-title">
                {footer.socialTitle || 'Suivez-nous'}
              </h3>

              {!hasSocial ? (
                <p className="theme1-footer-muted">
                  {footer.noSocial || "Liens sociaux non renseignés."}
                </p>
              ) : (
                <div className="theme1-footer-social">
                  {footer.facebookUrl && (
                    <a
                      className="theme1-footer-social-btn"
                      href={footer.facebookUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Facebook"
                      title="Facebook"
                    >
                      <span className="theme1-footer-social-ico">f</span>
                      <span>{footer.facebook || 'Facebook'}</span>
                    </a>
                  )}

                  {footer.instagramUrl && (
                    <a
                      className="theme1-footer-social-btn"
                      href={footer.instagramUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Instagram"
                      title="Instagram"
                    >
                      <span className="theme1-footer-social-ico">◎</span>
                      <span>{footer.instagram || 'Instagram'}</span>
                    </a>
                  )}

                  {footer.uberEatsUrl && (
                    <a
                      className="theme1-footer-social-btn"
                      href={footer.uberEatsUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Uber Eats"
                      title="Uber Eats"
                    >
                      <span className="theme1-footer-social-ico">🍴</span>
                      <span>{footer.uberEats || 'Uber Eats'}</span>
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Mentions / credits */}
            <div className="theme1-footer-card">
              <h3 className="theme1-footer-title">
                {footer.legalTitle || 'À propos'}
              </h3>

              <p className="theme1-footer-muted">
                {footer.legalText ||
                  "Les informations (horaires, carte) peuvent varier selon la période. Pour confirmation, contactez-nous."}
              </p>

              {footer.designedBy && (
                <p className="theme1-footer-credits">{footer.designedBy}</p>
              )}
            </div>
          </div>

          <div className="theme1-footer-divider" />

          <div className="theme1-footer-bottom">
            <span>
              © {year} <strong>{clientConfig.name}</strong> —{' '}
              {footer.rights || 'Tous droits réservés.'}
            </span>

            {footer.bottomLinks?.length ? (
              <div className="theme1-footer-bottom-links">
                {footer.bottomLinks.map((l, i) => (
                  <a key={i} href={l.href} target={l.target || undefined} rel={l.rel || undefined}>
                    {l.label}
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </footer>
    );
  }

  /* =========================
     THEME 2 — PREMIUM FOOTER (REWORK)
  ========================== */
  if (theme === 'theme2') {
    const brand = clientConfig.name || 'Restaurant';

    const hasSocial = !!footer.facebookUrl || !!footer.instagramUrl || !!footer.uberEatsUrl;

    // Optionnel : liens “footer.bottomLinks” comme theme1
    const bottomLinks = Array.isArray(footer.bottomLinks) ? footer.bottomLinks : [];

    return (
      <footer className="theme2-footer">
        <div className="theme2-footer-container">
          {/* Top brand row */}
          <div className="theme2-footer-head">
            <div className="theme2-footer-brand">
              <div className="theme2-footer-brand-name">{brand}</div>
              <p className="theme2-footer-brand-sub">
                {footer.tagline || "Cuisine maison • Produits frais • Service chaleureux"}
              </p>
            </div>

            <div className="theme2-footer-ctas">
              <a className="theme2-footer-btn theme2-footer-btn--ghost" href="#menu">
                {footer.ctaMenu || "Voir la carte"}
              </a>
              <a className="theme2-footer-btn" href="#reservation">
                {footer.cta || "Réserver"}
              </a>
            </div>
          </div>

          <div className="theme2-footer-grid">
            {/* Infos */}
            <div className="theme2-footer-card">
              <h3 className="theme2-footer-title">{footer.information || "Informations"}</h3>

              <ul className="theme2-footer-list">
                <li>
                  <span className="theme2-footer-ico">📍</span>
                  <span>{footer.address || "Adresse non renseignée"}</span>
                </li>
                <li>
                  <span className="theme2-footer-ico">📞</span>
                  <span>{footer.phone || "Téléphone non renseigné"}</span>
                </li>
                <li>
                  <span className="theme2-footer-ico">🕒</span>
                  <span>{footer.hours || "Horaires non renseignés"}</span>
                </li>
              </ul>
            </div>

            {/* Social */}
            <div className="theme2-footer-card">
              <h3 className="theme2-footer-title">{footer.socialTitle || "Suivez-nous"}</h3>

              {!hasSocial ? (
                <p className="theme2-footer-muted">
                  {footer.noSocial || "Liens sociaux non renseignés."}
                </p>
              ) : (
                <div className="theme2-footer-social">
                  {footer.facebookUrl && (
                    <a className="theme2-footer-social-btn" href={footer.facebookUrl} target="_blank" rel="noreferrer">
                      <span className="theme2-footer-social-ico">f</span>
                      <span>{footer.facebook || "Facebook"}</span>
                    </a>
                  )}
                  {footer.instagramUrl && (
                    <a className="theme2-footer-social-btn" href={footer.instagramUrl} target="_blank" rel="noreferrer">
                      <span className="theme2-footer-social-ico">◎</span>
                      <span>{footer.instagram || "Instagram"}</span>
                    </a>
                  )}
                  {footer.uberEatsUrl && (
                    <a className="theme2-footer-social-btn" href={footer.uberEatsUrl} target="_blank" rel="noreferrer">
                      <span className="theme2-footer-social-ico">🍴</span>
                      <span>{footer.uberEats || "Uber Eats"}</span>
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Mini legal / trust */}
            <div className="theme2-footer-card">
              <h3 className="theme2-footer-title">{footer.legalTitle || "À propos"}</h3>
              <p className="theme2-footer-muted">
                {footer.legalText ||
                  "Les informations (horaires, carte) peuvent varier selon la période. Pour confirmation, contactez-nous."}
              </p>
              {footer.designedBy ? <p className="theme2-footer-credits">{footer.designedBy}</p> : null}
            </div>
          </div>

          <div className="theme2-footer-divider" />

          <div className="theme2-footer-bottom">
            <div className="theme2-footer-copy">
              © {year} <strong>{brand}</strong> — {footer.rights || "Tous droits réservés."}
            </div>

            {bottomLinks.length ? (
              <div className="theme2-footer-links">
                {bottomLinks.map((l, i) => (
                  <a key={i} href={l.href} target={l.target || undefined} rel={l.rel || undefined}>
                    {l.label}
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </footer>
    );
  }

  /* =========================
     AUTRES THEMES — INCHANGÉS
  ========================== */
  if (theme === 'theme3' || theme === 'theme4') {
    return (
      <footer className="theme3-footer">
        <div className="theme3-container">
          <div className="theme3-footer-columns">
            <div className="theme3-footer-col">
              <i className="fas fa-map-marker-alt"></i>
              <div>
                <strong>{footer.addressTitle || "Adresse"}</strong>
                <p>{footer.address || "Non renseignée"}</p>
              </div>
            </div>
            <div className="theme3-footer-col">
              <i className="fas fa-phone-alt"></i>
              <div>
                <strong>{footer.contactTitle || "Contact"}</strong>
                <p>{footer.phone || "Non renseigné"}</p>
                <p>{footer.email || "Non renseigné"}</p>
              </div>
            </div>
            <div className="theme3-footer-col">
              <i className="fas fa-clock"></i>
              <div>
                <strong>{footer.hoursTitle || "Horaires"}</strong>
                <p>{footer.hours || "Non renseignés"}</p>
              </div>
            </div>
            <div className="theme3-footer-col">
              <strong>{footer.socialTitle || "Suivez-nous"}</strong>
              <div className="theme3-footer-social">
                {footer.facebookUrl && (
                  <a href={footer.facebookUrl} target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                )}
                {footer.instagramUrl && (
                  <a href={footer.instagramUrl} target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-instagram"></i>
                  </a>
                )}
                {footer.uberEatsUrl && (
                  <a href={footer.uberEatsUrl} target="_blank" rel="noopener noreferrer">
                    <i className="fas fa-utensils"></i>
                  </a>
                )}
              </div>
            </div>
          </div>

          <hr />

          <p className="theme3-footer-copy">
            &copy; {year} <strong>{clientConfig.name}</strong>. {footer.rights || "Tous droits réservés."}<br />
            <span className="theme3-footer-credits">{footer.designedBy || ""}</span>
          </p>
        </div>
      </footer>
    );
  }

  if (theme === 'theme5') {
    return (
      <footer className="theme5-footer">
        <div className="theme5-footer-top">
          <div className="theme5-footer-logo">{clientConfig.name}</div>

          <div className="theme5-footer-links">
            <a href="#home">Accueil</a>
            <a href="#speciality">Spécialités</a>
            <a href="#menu">Carte</a>
            <a href="#reservation">Réservation</a>
            <a href="#contact">Contact</a>
          </div>

          <a href="#reservation" className="theme5-footer-btn">Commander</a>
        </div>

        <hr className="theme5-footer-divider" />

        <div className="theme5-footer-bottom">
          &copy; {year} <strong>{clientConfig.name}</strong>. {footer.rights || "Tous droits réservés."}
          <span className="theme5-footer-credits">{footer.designedBy || ""}</span>
        </div>
      </footer>
    );
  }

  if (theme === 'theme6') {
    return (
      <footer className="theme6-footer">
        <div className="theme6-footer-container">
          <div className="theme6-footer-logo">{clientConfig.name}</div>
          <div className="theme6-footer-social">
            {footer.facebookUrl && (
              <a href={footer.facebookUrl} target="_blank" rel="noreferrer">
                <i className="fab fa-facebook-f"></i>
              </a>
            )}
            {footer.instagramUrl && (
              <a href={footer.instagramUrl} target="_blank" rel="noreferrer">
                <i className="fab fa-instagram"></i>
              </a>
            )}
            {footer.uberEatsUrl && (
              <a href={footer.uberEatsUrl} target="_blank" rel="noreferrer">
                <i className="fas fa-utensils"></i>
              </a>
            )}
          </div>
        </div>

        <div className="theme6-footer-bottom">
          &copy; {year} {clientConfig.name} — {footer.rights || "Tous droits réservés."}
        </div>
      </footer>
    );
  }

  return null;
};

export default Footer;
